import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
    GetActiveManagementRequestParams,
    IntervalReportSettings,
    useActiveManagementApi,
    useApi,
    useReportsApi,
} from '@/hooks'
import {
    ExportErrorResponse,
    ExportLimitErrorDetails,
    StartExportRequest,
} from '@/hooks/useApi/useActiveManagementApi/types'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useTranslation } from 'react-i18next'
import {
    DEFAULT_ACTIVE_MANAGEMENT_FILTERING,
    DEFAULT_INTERVAL_REPORT_SETTINGS,
} from '@/utils/constants'
import { mapIntervalDataQueryFromGetParams } from '@/features/ActiveManagement/utils/mapIntervalDataQueryFromGetParams'
import {
    EmailExportModalParams,
    useEmailExportModalParams,
} from '@/components/organisms/EmailExportModal/useEmailExportModalParams'
import { DailyEmailExportLimitModalContent } from '@/components/organisms/EmailExportModal/DailyEmailExportLimitModalContent'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import { processIntervalDataFilters } from '../utils/processIntervalDataFilters'

type IntervalDataExportMeta = {
    errorIdentifier?: string
    count?: number
    limit?: number
}

export type ExportIntervalDataReportEmailModalProps = EmailExportModalParams & {
    onSubmitExport: () => Promise<void>
    onSubmitExportError?: (error: unknown) => void
}

export const useExportIntervalDataReport = (
    reportId: string
): {
    exportIntervalDataReport: () => void
    emailExportModalProps: ExportIntervalDataReportEmailModalProps
    isExporting: boolean
} => {
    const addAlert = useToastStore((state) => state.addAlert)
    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const { t } = useTranslation('translations')
    const triggered = useRef(false)
    const [isExporting, setIsExporting] = useState(false)
    const {
        exportByEmailOpen,
        onExportByEmailClose,
        onExportByEmailSuccess,
        reportGenerationOpen,
        onReportGenerationClose,
        onExportByEmailOpen,
    } = useEmailExportModalParams()

    const { useGetReport } = useReportsApi()
    const { useStartExportMutation } = useApi()
    const customerId = useAuthStore((state) => state.customer?.customerId)
    const startExportMutation = useStartExportMutation()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const { useIntervalDataStreamedQuery } = useActiveManagementApi()

    const reportSettings = useMemo((): IntervalReportSettings => {
        if (
            report?.settings &&
            typeof report.settings === 'object' &&
            'dataGrouping' in report.settings &&
            'dataType' in report.settings &&
            'timeInterval' in report.settings
        ) {
            return report.settings
        }
        return DEFAULT_INTERVAL_REPORT_SETTINGS
    }, [report?.settings])

    const params = useMemo((): Partial<GetActiveManagementRequestParams> => {
        if (!report?.filters) {
            return DEFAULT_ACTIVE_MANAGEMENT_FILTERING
        }

        const processedFilters = processIntervalDataFilters(report.filters)

        return {
            ...processedFilters,
            type: 'utilityData',
            timezone: reportSettings.timezone,
            dataType: reportSettings.dataType,
            dataGrouping: reportSettings.dataGrouping,
            timeInterval: reportSettings.timeInterval,
        }
    }, [report?.filters, reportSettings])

    const {
        data: intervalData,
        refetch: refetchIntervalData,
        downloadXlsx,
    } = useIntervalDataStreamedQuery(params, {
        enabled: !!report && triggered.current,
        reportSettings,
    })

    const downloadData = useCallback(() => {
        downloadXlsx()
            .catch((error: unknown) => {
                const meta = (error ?? {}) as IntervalDataExportMeta
                const { errorIdentifier } = meta

                const isLimitExceeded = errorIdentifier === 'limitExceeded'

                if (isLimitExceeded) {
                    onExportByEmailOpen()
                } else {
                    addAlert({
                        variant: 'error',
                        title: t('reporting.export_report.error_toast.title'),
                        description: t(
                            'reporting.export_report.error_toast.description'
                        ),
                    })
                }
            })
            .finally(() => {
                setIsExporting(false)
            })
    }, [addAlert, downloadXlsx, onExportByEmailOpen, t])

    useEffect(() => {
        if (triggered.current && report) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, intervalData, report])

    const exportIntervalDataReport = () => {
        triggered.current = true
        setIsExporting(true)

        if (!report) {
            void refetchReport()
            return
        }

        if (!intervalData) {
            void refetchIntervalData()
            return
        }

        triggered.current = false
        downloadData()
    }

    const isDailyLimitExceeded = (
        currentCount?: number,
        limit?: number
    ): boolean => {
        return (
            typeof currentCount === 'number' &&
            typeof limit === 'number' &&
            currentCount >= limit
        )
    }

    const openDailyLimitReachedModal = useCallback(() => {
        const closeModal = () => {
            setModalState({ isOpen: false })
        }

        setModalState({
            isOpen: true,
            title: t('common.exportByEmail.daily_limit_reached.title'),
            description: t(
                'common.exportByEmail.daily_limit_reached.description'
            ),
            icon: 'warning',
            iconClassName: 'text-orange-500',
            content: () =>
                React.createElement(DailyEmailExportLimitModalContent, {
                    onClose: closeModal,
                }),
            onClose: closeModal,
        })
    }, [t, setModalState])

    const showLimitReachedFlow = useCallback(() => {
        onExportByEmailClose()
        setTimeout(openDailyLimitReachedModal, 0)
    }, [onExportByEmailClose, openDailyLimitReachedModal])

    const isLimitError = (error: unknown): boolean => {
        if (!axios.isAxiosError(error)) return false

        const status = error.response?.status
        const responseData = error.response?.data as
            | { details?: ExportLimitErrorDetails; message?: string }
            | undefined

        const details = responseData?.details

        const bodyHasLimitDetails =
            details !== undefined &&
            typeof details.currentCount === 'number' &&
            typeof details.limit === 'number' &&
            details.currentCount >= details.limit

        const bodySuggestsLimit =
            typeof responseData?.message === 'string' &&
            responseData.message.toLowerCase().includes('limit')

        return (
            status === 429 ||
            status === 403 ||
            bodyHasLimitDetails ||
            bodySuggestsLimit
        )
    }

    const handleExportByEmailSubmitError = useCallback(
        (error: unknown) => {
            if (isLimitError(error)) {
                showLimitReachedFlow()
                return
            }

            onExportByEmailClose()
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        },
        [showLimitReachedFlow, onExportByEmailClose, addAlert, t]
    )

    const handleSubmitExportByEmail = useCallback(async () => {
        const customerIdNum = Number(customerId)
        if (
            !report ||
            !params.startDate ||
            !params.endDate ||
            !params.type ||
            Number.isNaN(customerIdNum)
        ) {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
            return
        }
        const query = mapIntervalDataQueryFromGetParams(params, reportSettings)
        const payload: StartExportRequest = {
            exportType: 'INTERVAL_DATA',
            customerId: customerIdNum,
            query,
        }
        const result = (await startExportMutation.mutateAsync(
            payload
        )) as unknown
        const hasErrorType =
            typeof result === 'object' &&
            result !== null &&
            'type' in result &&
            (result as Record<string, unknown>).type === 'error'
        if (hasErrorType) {
            const { details } = result as ExportErrorResponse
            if (isDailyLimitExceeded(details?.currentCount, details?.limit)) {
                showLimitReachedFlow()
                return
            }
        }
        onExportByEmailSuccess()
    }, [
        customerId,
        report,
        params,
        reportSettings,
        addAlert,
        t,
        startExportMutation,
        onExportByEmailSuccess,
        showLimitReachedFlow,
    ])

    return {
        exportIntervalDataReport,
        isExporting,
        emailExportModalProps: {
            exportByEmailOpen,
            onExportByEmailClose,
            onExportByEmailSuccess,
            reportGenerationOpen,
            onReportGenerationClose,
            onExportByEmailOpen,
            onSubmitExport: handleSubmitExportByEmail,
            onSubmitExportError: handleExportByEmailSubmitError,
        },
    }
}
